import React, { useEffect, useMemo, useState } from "react";
import { Clock, ListMusic } from "lucide-react";
import { STATION } from "../mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NL_DAYS = ["zo", "ma", "di", "wo", "do", "vr", "za"];
const NL_MONTHS_SHORT = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];

const todayStr = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
};

const dayLabel = (dateStr) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((today - date) / 86400000);
  if (diff === 0) return "Vandaag";
  if (diff === 1) return "Gisteren";
  return `${NL_DAYS[date.getDay()]} ${d} ${NL_MONTHS_SHORT[m - 1]}`;
};

const Playlist = () => {
  const [days, setDays] = useState([]);
  const [selectedDay, setSelectedDay] = useState(todayStr());
  const [data, setData] = useState({ date: "", hours: [], tracks: [] });
  const [selectedHour, setSelectedHour] = useState(null);

  // Load the last 7 days for the day selector.
  useEffect(() => {
    let active = true;
    const loadDays = async () => {
      try {
        const res = await fetch(`${API}/playlist/days`);
        if (!res.ok) return;
        const d = await res.json();
        if (active && d && Array.isArray(d.days)) setDays(d.days);
      } catch (e) {
        /* ignore */
      }
    };
    loadDays();
    const id = setInterval(loadDays, 60 * 1000);
    return () => { active = false; clearInterval(id); };
  }, []);

  // Load tracks for the selected day (refreshes live for today).
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const res = await fetch(`${API}/playlist?date=${selectedDay}`);
        if (!res.ok) return;
        const d = await res.json();
        if (active && d) {
          setData(d);
          setSelectedHour((prev) => {
            if (prev != null && d.hours.includes(prev)) return prev;
            return d.hours.length ? d.hours[0] : null;
          });
        }
      } catch (e) {
        /* ignore */
      }
    };
    load();
    const id = setInterval(load, 60 * 1000);
    return () => { active = false; clearInterval(id); };
  }, [selectedDay]);

  const tracksForHour = useMemo(
    () => data.tracks.filter((t) => selectedHour == null || t.hour === selectedHour),
    [data.tracks, selectedHour]
  );

  return (
    <div className="min-h-screen">
      <section className="relative overflow-hidden pt-40 pb-16">
        <div className="absolute -top-40 left-1/2 h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-white/25 blur-3xl" />
        <div className="relative mx-auto max-w-3xl px-5 sm:px-8 text-center animate-fade-up">
          <span className="inline-flex items-center gap-2 rounded-full glass-tint px-4 py-1.5 text-sm font-semibold text-white">
            <ListMusic className="h-4 w-4" /> Playlist
          </span>
          <h1 className="mt-6 font-heading text-5xl font-extrabold text-white sm:text-6xl">Recent gedraaid</h1>
          <p className="mx-auto mt-6 max-w-xl text-lg text-white/90">
            Bekijk per dag en per uur welke nummers op All-In Radio zijn afgespeeld (afgelopen 7 dagen).
          </p>
        </div>
      </section>

      <section className="pb-28">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          {/* Day selector (last 7 days) */}
          {days.length > 0 ? (
            <div className="mb-4 flex flex-wrap gap-2" data-testid="day-selector">
              {days.map((dObj) => {
                const active = dObj.date === selectedDay;
                return (
                  <button
                    key={dObj.date}
                    type="button"
                    data-testid={`day-tab-${dObj.date}`}
                    onClick={() => { setSelectedDay(dObj.date); setSelectedHour(null); }}
                    disabled={dObj.count === 0}
                    className={`rounded-full px-4 py-2 text-sm font-bold transition-all ${
                      active
                        ? "bg-white text-[#1E6BE6] shadow"
                        : dObj.count === 0
                        ? "cursor-not-allowed border border-white/10 bg-white/5 text-white/40"
                        : "border border-white/20 bg-white/15 text-white hover:bg-white/25"
                    }`}
                  >
                    {dayLabel(dObj.date)}
                  </button>
                );
              })}
            </div>
          ) : null}

          {/* Hour filter */}
          {data.hours.length > 0 ? (
            <div className="mb-6 flex flex-wrap gap-2" data-testid="hour-selector">
              {data.hours.map((h) => {
                const label = `${String(h).padStart(2, "0")}:00`;
                const active = h === selectedHour;
                return (
                  <button
                    key={h}
                    type="button"
                    data-testid={`hour-tab-${h}`}
                    onClick={() => setSelectedHour(h)}
                    className={`rounded-full px-4 py-2 text-sm font-bold transition-all ${
                      active
                        ? "bg-white text-[#1E6BE6] shadow"
                        : "bg-white/15 text-white hover:bg-white/25 border border-white/20"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          ) : null}

          <div className="rounded-3xl bg-[#4288F5] border border-white/15 p-4 sm:p-6 shadow-[0_18px_40px_-22px_rgba(8,30,80,0.5)]">
            {tracksForHour.length === 0 ? (
              <p className="px-2 py-10 text-center text-white/80">
                Geen nummers geregistreerd voor deze dag.
              </p>
            ) : (
              <div className="divide-y divide-white/12">
                {tracksForHour.map((t, i) => (
                  <div key={`${t.iso}-${i}`} className="flex items-center gap-4 py-3">
                    <span className="inline-flex w-14 shrink-0 items-center gap-1 font-heading text-xs font-bold text-white/80">
                      <Clock className="h-3.5 w-3.5" /> {t.time}
                    </span>
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-[#2E7DF7] to-[#4DA6FF] ring-1 ring-white/30">
                      {t.artwork ? (
                        <img
                          key={t.artwork}
                          src={t.artwork}
                          alt={t.song || t.raw}
                          className="h-full w-full object-cover"
                          data-testid="playlist-artwork"
                          onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "block"; }}
                        />
                      ) : null}
                      <img
                        src={STATION.logoFull}
                        alt=""
                        className="h-full w-full object-contain p-2"
                        style={{ display: t.artwork ? "none" : "block" }}
                      />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-white">{t.song || t.raw}</p>
                      {t.artist ? <p className="text-sm text-white/75">{t.artist}</p> : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Playlist;
